import { syncMediaCaption } from '@/lib/admin-api';
import { isGifUrl } from '@/lib/image-media-utils';

const MEDIA_PATH_PATTERN =
  /^(?:storage\/)?(haber|manset|video|icerik|user-submissions)\/\d{4}\/\d{2}(?:\/\d{2})?/;

const MEDIA_FOLDERS = [
  'haber',
  'manset',
  'video',
  'icerik',
  'user-submissions',
] as const;

export function extractRelativeMediaPath(src: string): string | null {
  const trimmed = src.trim();
  if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return null;
  }

  const noQuery = trimmed.split('?')[0].split('#')[0];
  const cleaned = noQuery.replace(/^\/+/, '').replace(/^storage\//, '');

  if (MEDIA_PATH_PATTERN.test(cleaned)) {
    // Return full relative key including filename when present
    return cleaned.replace(/^storage\//, '');
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const fromQuery = url.searchParams.get('path')?.trim();
      if (fromQuery) {
        const qClean = fromQuery.replace(/^\/+/, '').replace(/^storage\//, '');
        if (
          MEDIA_PATH_PATTERN.test(qClean) ||
          (MEDIA_FOLDERS as readonly string[]).some((f) =>
            qClean.startsWith(`${f}/`),
          )
        ) {
          return qClean;
        }
      }
      const parts = url.pathname.split('/').filter(Boolean);
      // storage/icerik/... → icerik/... (storage kökünü atla)
      const folderIdx = parts.findIndex((part) =>
        (MEDIA_FOLDERS as readonly string[]).includes(part),
      );
      if (folderIdx >= 0) {
        return parts.slice(folderIdx).join('/');
      }
    } catch {
      return null;
    }
  }

  // Relative key without date pattern strictness — still accept known roots
  if (
    (MEDIA_FOLDERS as readonly string[]).some((f) => cleaned.startsWith(`${f}/`))
  ) {
    return cleaned;
  }

  return null;
}

export async function syncMediaCaptionFromEditor(
  src: string,
  caption: string,
): Promise<void> {
  const trimmed = caption.trim();
  if (!trimmed || isGifUrl(src)) return;

  const path = extractRelativeMediaPath(src);
  if (!path) return;

  try {
    await syncMediaCaption(path, trimmed);
  } catch {
    // Sessiz — kayıt sırasında toplu senkron yapılır
  }
}

export function collectCaptionPairsFromDocument(
  doc: Document,
): Array<{ path: string; caption: string }> {
  const pairs: Array<{ path: string; caption: string }> = [];
  const seen = new Set<string>();

  doc.body.querySelectorAll('figure').forEach((figure) => {
    const img = figure.querySelector('img');
    const figcaption = figure.querySelector('figcaption');
    if (!img || !figcaption) return;

    const src = img.getAttribute('src') || '';
    if (isGifUrl(src)) return;

    const path = extractRelativeMediaPath(src);
    if (!path) return;

    const caption = figcaption.textContent?.trim() || '';
    if (!caption || seen.has(path)) return;

    seen.add(path);
    pairs.push({ path, caption });
  });

  return pairs;
}
